import React, { useContext, useState, useEffect } from 'react';
import { auth, database } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { ref, get, onValue } from 'firebase/database';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [employeeContext, setEmployeeContext] = useState(null); // { companyId, employeeId }
  const [userData, setUserData] = useState(null); // Full employee record
  const [userRole, setUserRole] = useState('employee'); // 'employee' or 'manager'
  const [loading, setLoading] = useState(true);

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    setEmployeeContext(null);
    setUserData(null);
    setUserRole('employee');
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // 1. Fetch Reverse Index to get Company/Employee IDs
        try {
          const indexRef = ref(database, `employees_index/${user.uid}`);
          // Use onValue to keep context in sync if ids change (unlikely but good practice)
          onValue(indexRef, (snapshot) => {
             if (snapshot.exists()) {
                const context = snapshot.val();
                setEmployeeContext(context);

                // 2. Fetch Employee Data using the IDs
                const empRef = ref(database, `companies/${context.companyId}/employees/${context.employeeId}`);
                onValue(empRef, (empSnap) => {
                    if (empSnap.exists()) {
                        const data = empSnap.val();
                        setUserData(data);
                        
                        // 3. Determine Role
                        const permissions = data.portal?.permissions || [];
                        if (permissions.includes('manager')) {
                            setUserRole('manager');
                        } else {
                            setUserRole('employee');
                        }
                    }
                });
             } else {
                console.error("No employee index found for user");
             }
          });
        } catch (error) {
          console.error("Error fetching employee context:", error);
        }
      } else {
        setEmployeeContext(null);
        setUserData(null);
        setUserRole('employee');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    employeeContext,
    userData,
    userRole,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div style={{ 
          height: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          background: '#031128', 
          color: 'white',
          fontFamily: 'Montserrat'
        }}>
          Loading Application...
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
